/* eslint-disable @typescript-eslint/no-explicit-any */
import { IEvidence, LearningObjective } from "@interfaces/Interfaces";

/**
 * Calculates the number of unique learning outcomes achieved based on the provided evidence.
 *
 * This function iterates over the given evidence and checks for learning outcomes
 * that are present in the "LearningObjectivesTickboxes" section of the evidence JSON.
 * It matches these outcomes with the list of learning outcomes provided and counts
 * the number of unique matches.
 *
 * @param learningOutcomes - An array of LearningObjective objects representing the
 *                           desired learning outcomes to check against.
 * @param evidence - An array of IEvidence objects containing evidence data with
 *                   potential learning outcome information.
 * @returns The number of unique learning outcomes achieved based on the evidence.
 */

/**
 * 
type LearningOutcome = {
  id: string;
  name: string;
  type?: string;
};
*/

export function getLearningOutcomesAchieved(
  learningOutcomes: LearningObjective[],
  programEvidence: IEvidence[],
  useAutoLOs: boolean
): number {
  const outcomesIds = new Set<string>();

  learningOutcomes.forEach((lo) => {
    const numberRequired = lo.NumberRequired ?? 1;
    let numberDone = 0;

    programEvidence.forEach((evidence) => {
      if (evidence.evidenceJSON["LearningObjectivesTickboxes"]) {
        Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
          if (outcome.id === lo.ID) {
            if (useAutoLOs) {
              if (outcome.type === "automatic") {
                numberDone += 1;
              } else if (typeof outcome.type === "undefined") {
                numberDone += 1;
              }
            } else {
              numberDone += 1;
            }
          }
        });
      }

      if (evidence.evidenceJSON["LearningObjectivesTickboxes Optional"]) {
        Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes Optional"].selectedOutcomes).forEach(
          (outcome: any) => {
            if (outcome.id === lo.ID) {
              if (useAutoLOs) {
                if (outcome.type === "automatic") {
                  numberDone += 1;
                } else if (typeof outcome.type === "undefined") {
                  numberDone += 1;
                }
              } else {
                numberDone += 1;
              }
            }
          }
        );
      }
    });

    if (numberDone >= numberRequired) {
      outcomesIds.add(lo.ID);
    }
  });

  return outcomesIds.size;
}

export function getLearningOutcomesPartiallyMet(
  learningOutcomes: LearningObjective[],
  programEvidence: IEvidence[],
  useAutoLOs: boolean
): number {
  let count = 0;

  learningOutcomes.forEach((lo) => {
    const numberRequired = lo.NumberRequired ?? 1;
    let numberDone = 0;

    programEvidence.forEach((evidence) => {
      if (evidence.evidenceJSON["LearningObjectivesTickboxes"]) {
        Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
          if (outcome.id === lo.ID) {
            if (useAutoLOs) {
              if (outcome.type === "automatic") {
                numberDone += 1;
              } else if (typeof outcome.type === "undefined") {
                numberDone += 1;
              }
            } else {
              numberDone += 1;
            }
          }
        });
      }

      if (evidence.evidenceJSON["LearningObjectivesTickboxes Optional"]) {
        Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes Optional"].selectedOutcomes).forEach(
          (outcome: any) => {
            if (outcome.id === lo.ID) {
              if (useAutoLOs) {
                if (outcome.type === "automatic") {
                  numberDone += 1;
                } else if (typeof outcome.type === "undefined") {
                  numberDone += 1;
                }
              } else {
                numberDone += 1;
              }
            }
          }
        );
      }
    });

    if (numberDone > 0 && numberDone < numberRequired) {
      count += 1;
    }
  });

  return count;
}

export function learningOutcomesHavePartialState(learningOutcomes: LearningObjective[]): boolean {
  return learningOutcomes.some((lo) => (lo.NumberRequired ?? 1) > 1);
}

export function getOccurrencesOfLearningOutcome(learningOutcome: LearningObjective, evidence: IEvidence[]): number {
  let sum = 0;

  evidence.forEach((evidence) => {
    if (evidence.evidenceJSON["LearningObjectivesTickboxes"]) {
      Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
        if (outcome.id === learningOutcome.ID) sum += 1;
      });
    }

    if (evidence.evidenceJSON["LearningObjectivesTickboxes Optional"]) {
      Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes Optional"].selectedOutcomes).forEach(
        (outcome: any) => {
          if (outcome.id === learningOutcome.ID) sum += 1;
        }
      );
    }
  });

  return sum;
}

export function getAutomaticOccurrencesOfLearningOutcome(
  learningOutcome: LearningObjective,
  evidence: IEvidence[]
): number {
  let sum = 0;

  evidence.forEach((evidence) => {
    if (evidence.evidenceJSON["LearningObjectivesTickboxes"]) {
      Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
        if (outcome.id === learningOutcome.ID) {
          if (outcome.type === "automatic") {
            sum += 1;
          } else if (typeof outcome.type === "undefined") {
            sum += 1;
          }
        }
      });
    }
  });

  return sum;
}

export function getManualOccurrencesOfLearningOutcome(
  learningOutcome: LearningObjective,
  evidence: IEvidence[]
): number {
  let sum = 0;

  evidence.forEach((evidence) => {
    if (evidence.evidenceJSON["LearningObjectivesTickboxes"]) {
      Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
        if (outcome.id === learningOutcome.ID) {
          if (outcome.type === "manual" || typeof outcome.type === "undefined") {
            sum += 1;
          }
        }
      });
    }

    if (evidence.evidenceJSON["LearningObjectivesTickboxes Optional"]) {
      Array.from(evidence.evidenceJSON["LearningObjectivesTickboxes Optional"].selectedOutcomes).forEach(
        (outcome: any) => {
          if (outcome.id === learningOutcome.ID) {
            if (outcome.type === "manual" || typeof outcome.type === "undefined") {
              sum += 1;
            }
          }
        }
      );
    }
  });

  return sum;
}
